import { useSearchParams } from "react-router-dom";
import { Page } from "../../../types/GlobalInterfaces";
import { View } from "../../../types/PageInterfaces";
import BasicTableView from "../../../views/supportal/CloudListView/BasicTableView";
import AppPageHeader from "../../Headers/AppPageHeader";
import { BasicSearch } from "../../../views/cloud/CloudSettingsHome";
import { useState } from "react";
import { Button, Input } from "reactstrap";
import useCloudPermissions from "../../../hooks/useCloudPermissions";
import useWorkspacePermissions from "../../../hooks/useWorkspacePermissions";
import usePagePermissions from "../../../hooks/usePagePermissions";
import IconButton from "../../../elements/IconButton";
import useQueriedRecords from "../../../api/api-hooks/records/useQueriedRecords";
import { DrawerProps } from "../../Drawers/DrawerTypes";
import AppDrawer from "../../Drawers/AppDrawer";
import DocumentPicker from "../../CRM/DocumentPicker";
import RequiredTitle from "../../../elements/RequiredTitle";
import useUser from "../../../api/api-hooks/auth/useUser";
import TenantAPI from "../../../api/api/TenantAPI";

import useUpdateView from "../../../api/api-hooks/views/useUpdateView";
import AppSpinner from "../../../elements/AppSpinner";
import { AppConfirm } from "../../../utils/PopupUtil";
import PageViewDocument from "./PageViewDocument";

interface Props {
  page: Page;
  view: View;
  viewLoading: boolean;
  parentViewID?: string;
}

export default function PageViewDocumentMultiple(props: Props) {
  const [params, setParams] = useSearchParams();

  const documentID = params.get("doc");

  if (documentID) {
    return (
      <PageViewDocument
        page={props.page}
        view={props.view}
        document={{ id: documentID, ModuleAPIName: "Documents" }}
        handleBack={() => {
          params.delete("doc");
          setParams(params);
        }}
      />
    );
  }

  return <PageViewDocumentList {...props} />;
}

interface TinyDoc {
  _id: string;
  _RecordName: string;
  Description?: string;
  Shared?: boolean;
}

function PageViewDocumentList({
  view,
  page,
  parentViewID,
  viewLoading,
}: Props) {
  const [search, setSearch] = useState("");
  const [params, setParams] = useSearchParams();
  const [addDocumentsOpen, setAddDocumentsOpen] = useState(false);
  const [deleteDocIds, setDeleteDocIds] = useState<string[]>([]);

  const { cPermissions } = useCloudPermissions();
  const { wPermissions } = useWorkspacePermissions();
  const { pPermissions } = usePagePermissions();
  const { updateView } = useUpdateView(page?.id!);

  const documentIds = view?.Documents?.map((d) => d.id);

  let hasPermissions =
    cPermissions.cloud_modify_page ||
    wPermissions.workspace_modify_page ||
    pPermissions.page_modify_page;

  const { records, recordsLoading } = useQueriedRecords({
    moduleAPIName: "Documents",
    disabled: viewLoading || !documentIds?.length,
    query: {
      Fields: ["_RecordName", "_id", "Description", "Shared"],
      Filters: [
        { Group: false, Field: "_id", Method: "in", Value: documentIds },
      ],
      OrderBy: {
        Field: "Created",
        Direction: "DESC",
      },
    },
  });

  const documents = records as TinyDoc[];
  const documentsLoading = recordsLoading && !!documentIds?.length;

  const filteredDocs = search
    ? documents.filter((d) => {
        return (
          d?._RecordName?.toLowerCase().includes(search.toLowerCase()) ||
          d?.Description?.toLowerCase().includes(search.toLowerCase())
        );
      })
    : documents;

  return (
    <>
      <div className="mt--3">
        <AppPageHeader
          title={view?.Name || "Documents"}
          description={view?.Description || `The documents added to this view`}
          customContent={
            <div className="d-flex justify-content-between align-items-center mt-2 mt-md-0">
              <div style={{ width: 300 }}>
                <BasicSearch
                  setSearchValue={setSearch}
                  placeholder={`Search Documents`}
                />
              </div>
              {hasPermissions && (
                <Button
                  className="ml-3"
                  color="primary"
                  onClick={() => setAddDocumentsOpen(true)}
                >
                  Add
                </Button>
              )}
            </div>
          }
        />
        <BasicTableView
          loading={viewLoading || documentsLoading}
          items={filteredDocs}
          title={undefined}
          getItemTitle={(d) => (
            <div>
              {d?._RecordName}
              {d?.Shared && (
                <i className="mdi mdi-share-variant text-primary ml-2" />
              )}
            </div>
          )}
          getItemSubtitle={(d) => d?.Description}
          onClick={(d) => selectDocument(d?._id)}
          CustomRenderer={
            hasPermissions
              ? (d) => (
                  <>
                    <IconButton
                      icon="close"
                      color="danger"
                      onClick={(e: any) => {
                        e.stopPropagation();
                        removeDocumentFromView(d._id);
                      }}
                      loading={deleteDocIds.includes(d._id)}
                    />
                  </>
                )
              : undefined
          }
        />
      </div>
      <AddDocumentDrawer
        open={addDocumentsOpen}
        onClose={() => setAddDocumentsOpen(false)}
        pageID={page?.id!}
        viewID={view?.id}
        parentViewID={parentViewID}
        documentIds={documentIds}
      />
    </>
  );

  function selectDocument(docID: string) {
    params.set("doc", docID);
    setParams(params);
  }

  async function removeDocumentFromView(docID: string) {
    const confirmed = await AppConfirm({
      title: "Remove Document From View?",
      text: "Are you sure you want to remove this document from this view?",
      confirmButtonText: "Remove",
    });

    if (!confirmed) return;

    setDeleteDocIds((prev) => [...prev, docID]);

    updateView({
      payload: {
        DocumentToDelete: docID,
      },
      viewID: view?.id,
      parentViewID,
      onSettled: () =>
        setDeleteDocIds((prev) => prev.filter((d) => d !== docID)),
    });
  }
}

function AddDocumentDrawer({
  open,
  onClose,
  pageID,
  viewID,
  parentViewID,
  documentIds,
}: {
  pageID: string;
  viewID: string;
  parentViewID?: string;
  documentIds: string[] | undefined;
} & DrawerProps) {
  const [document, setDocument] = useState("");
  const [documentName, setDocumentName] = useState("");
  const [error, setError] = useState("");

  const { user } = useUser();
  const { updateView, viewUpdating } = useUpdateView(pageID);
  const [updating, setUpdating] = useState(false);

  return (
    <AppDrawer
      open={open}
      onClose={onClose}
      headerTitle={"Adding Document to View"}
      error={error ? { message: error, onClear: () => setError("") } : null}
      headerOptions={
        <Button
          color="primary"
          onClick={addDocumentToView}
          disabled={document === "new" && !documentName}
        >
          {updating || viewUpdating ? (
            <AppSpinner color="white" size="sm" />
          ) : (
            "Add"
          )}
        </Button>
      }
      body={
        <>
          <DocumentPicker value={document} onChange={setDocument} required />

          {document === "new" && (
            <>
              <RequiredTitle title="Document Name" size="md" />
              <Input
                type="text"
                placeholder="Document Name"
                onChange={(e) => setDocumentName(e.target.value)}
                autoFocus={true}
              />
            </>
          )}
        </>
      }
    />
  );

  function handleClose() {
    setDocument("");
    setDocumentName("");
    setError("");
    onClose();
  }

  function addDocumentToView() {
    setError("");

    if (Array.isArray(documentIds) && documentIds.includes(document)) {
      return setError("Document was already added to this view.");
    }

    // Add an existing document to the View (Editable across GC)
    if (document && document !== "new") {
      return updateView({
        payload: {
          Documents: { id: document },
        },
        viewID,
        parentViewID,
        onSuccess: handleClose,
      });
    }

    if (!documentName) return;

    setUpdating(true);

    // NEW Document - Upload as Record first.
    let documentPayload = {
      Name: documentName,
      Owner: {
        ModuleAPIName: "Contacts",
        id: user?.id,
      },
    };

    TenantAPI.insertRecord("Documents", documentPayload)
      .then((data: any) => {
        let record = data?.data?.data?.data;

        updateView({
          payload: {
            Documents: { id: record?.id },
          },
          viewID,
          parentViewID,
          onSuccess: handleClose,
        });
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => setUpdating(false));
  }
}
